import {
  Component,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { FiltersMagicBoxStateInterface } from "../../../store/actions/filters-magic-box.actions";
import { MagicBoxService } from "../../../services/magic-box.service";
import { Rule } from "../../../models/rule";
import { MdDialog } from "@angular/material";
import { Value } from "../../../models/value";
import { Md2Dialog } from "md2";
import { User } from "../../../models/user";
import { Router } from "@angular/router";
import { RulesActions } from "../../../store/actions/rules.actions";
import { AdminService } from "../../../services/admin.service";
import { Subscription } from "rxjs/Subscription";
import { RulesService } from "../../../services/rules.service";
import * as _ from "lodash";
import { RuleDuplicateWarningComponent } from "../../../modals/rule-duplicate-warning/rule-duplicate-warning.component";
import { FeatureCountries } from "../../../models/featureCountries";
import { SuccessComponent } from "../../../modals/success/success.component";

@Component({
  selector: "app-magic-box-search",
  templateUrl: "./magic-box-search.component.html",
  styleUrls: ["./magic-box-search.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class MagicBoxSearchComponent implements OnDestroy {
  loading: boolean;
  index: number;
  limit: number;
  orderByDescending: boolean;
  orderOn: number;
  rules: Rule[];
  currentPage: number;
  total: number;
  filters: FiltersMagicBoxStateInterface;
  referentialTypes: Value[];
  currentUser: User;
  editMode: boolean;
  subscriptions: Subscription[];
  filtersMagicBoxSubscription: Subscription;
  moreLoading: boolean;
  canEdit: string[];
  canView: string[];
  canEditMagicBox: boolean;
  isNonSportiveClassif: boolean;
  disabledEditing: boolean = false;
  features: FeatureCountries[];
  languageId: number;

  @ViewChild("table") table: any;
  @ViewChild("duplicate") duplicate: Md2Dialog;

  /**
   * Initialize variables and load data
   * @param {Store<any>} store
   * @param {MagicBoxService} magicBoxService
   * @param {Router} router
   * @param {RulesActions} rulesActions
   * @param {RulesService} rulesService
   * @param {AdminService} adminService
   * @param {MdDialog} mdDialog
   */
  constructor(
    private store: Store<any>,
    private magicBoxService: MagicBoxService,
    private router: Router,
    private rulesActions: RulesActions,
    private rulesService: RulesService,
    private adminService: AdminService,
    private mdDialog: MdDialog
  ) {
    this.loading = true;
    this.moreLoading = false;
    this.subscriptions = [];
    this.canEdit = [];
    this.canView = [];
    this.canEditMagicBox = false;
    this.disabledEditing = false;

    this.getUserRights();
    this.isNonSportiveClassif = false;

    setTimeout(() => {
      this.getReferentialTypes();
      this.getUserState();
      this.getRulesState();
    }, 100);

    this.subscriptions.push(
      this.rulesService.rulesUpdated.subscribe((update: boolean) => {
        if (update === true) {
          this.loading = true;
          this.init();
          this.loadRules();
        }
      })
    );
  }

  /**
   * On destroy event, clear the rules store and unsubscribe all the subscriptions
   */
  ngOnDestroy() {
    this.rulesActions.setRules([]);
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    if (this.filtersMagicBoxSubscription) {
      this.filtersMagicBoxSubscription.unsubscribe();
    }
  }

  /**
   * Fetch all referential types from the API
   */
  getReferentialTypes() {
    this.subscriptions.push(
      this.magicBoxService
        .getReferentialTypes(this.isNonSportiveClassif)
        .subscribe((types) => {
          this.referentialTypes = types;
        })
    );
  }

  /**
   * Get the current user from the store
   */
  getUserState() {
    this.subscriptions.push(
      this.store.select("userState").subscribe((user: User) => {
        if (user && user.currentLanguage) {
          this.currentUser = user;
          if (this.filtersMagicBoxSubscription) {
            this.filtersMagicBoxSubscription.unsubscribe();
          }
          this.getFiltersMagicBoxState();
        } else {
          this.currentUser = null;
        }
      })
    );
  }

  /**
   * Get rules from the store
   */
  getRulesState() {
    this.subscriptions.push(
      this.store.select("rulesState").subscribe((rules: Rule[]) => {
        this.rules = rules;
      })
    );
  }

  /**
   * Get the filters from the store
   */
  getFiltersMagicBoxState() {
    this.filtersMagicBoxSubscription = this.store
      .select("filtersMagicBoxState")
      .subscribe((filters: FiltersMagicBoxStateInterface) => {
        setTimeout(() => {
          this.init();
          this.loading = true;

          this.filters = filters;
          this.loadRules();
        }, 200);
      });
  }

  /**
   * Check the user rights for this component
   */
  getUserRights() {
    this.subscriptions.push(
      this.adminService.init().subscribe(() => {
        if (this.adminService.can("Magic Box Digital", "Edit")) {
          this.canEdit.push("Digital");
        }

        if (this.adminService.can("Magic Box Digital", "View")) {
          this.canView.push("Digital");
        }

        if (this.adminService.can("Magic Box Press", "Edit")) {
          this.canEdit.push("Press");
        }

        if (this.adminService.can("Magic Box Press", "View")) {
          this.canView.push("Press");
        }

        if (this.adminService.can("Magic Box Popup Next", "Edit")) {
          this.canEdit.push("Popup Next");
        }

        if (this.adminService.can("Magic Box Popup Next", "View")) {
          this.canView.push("Popup Next");
        }

        if (this.adminService.can("Magic Box Partners", "Edit")) {
          this.canEdit.push("Partners");
        }

        if (this.adminService.can("Magic Box Partners", "View")) {
          this.canView.push("Partners");
        }
        this.canEditMagicBox =
          this.adminService.can("Magic Box Digital", "View") ||
          this.adminService.can("Magic Box Press", "View") ||
          this.adminService.can("Magic Box Popup Next", "View") ||
          this.adminService.can("Magic Box Partners", "View");
      })
    );
  }

  /**
   * Init the parameters for the rules fetching
   */
  init() {
    this.currentPage = 1;
    this.index = 0;
    this.limit = 100;
    this.orderByDescending = true;
    this.orderOn = 5;
    this.rulesActions.setRules([]);
    this.loading = false;
    this.EditMagicBoxByLanguage();
  }

  // Check MagicBox Edit Authorization by language
  EditMagicBoxByLanguage() {
    this.disabledEditing =
      this.currentUser.currentLanguage.features.includes("DATA_EDIT_MAGICBOX");
  }

  /**
   * When a page is changed, calculate if we muse load more rules
   * @param event
   * @param indexes
   */
  onPage(event: any, indexes: any) {
    const totalShown = this.currentPage * this.limit;
    const percentScrolled = indexes.last / (totalShown / this.limit);
    if (percentScrolled > 80 && totalShown < this.total && !this.loading) {
      this.currentPage++;
      this.moreLoading = true;
      this.loadRules();
    }
  }

  /**
   * Fetch rules from API with the parameters defined
   */
  loadRules() {
    if (this.currentUser && this.currentUser.currentLanguage) {
      this.magicBoxService
        .search(
          this.currentUser.currentLanguage.id,
          (this.currentPage - 1) * this.limit,
          this.limit,
          this.orderByDescending,
          this.orderOn,
          this.filters,
          this.canView
        )
        .subscribe((res) => {
          if (!this.rules) {
            this.rules = [];
          }
          const rules = this.rules.concat(res.values);
          this.rulesActions.setRules(_.uniq(rules));
          this.total = res.values.length;
          this.loading = false;
          this.moreLoading = false;
        });
    }
  }

  /**
   * Delete the rule with an API call
   * @param {Rule} rule
   */
  deleteRule(rule: Rule) {
    this.magicBoxService.preDelete(rule).subscribe((res) => {
      if (res.success && res.success === 2) {
        this.loading = false;
        this.mdDialog.open(RuleDuplicateWarningComponent, {
          data: {
            message: res.message,
            values: res.values,
            rule,
            mode: "delete",
          },
        });
      }
    });
  }

  /**
   * Restore the rule
   * @param {Rule} rule
   */
  restoreRule(rule: Rule) {
    this.magicBoxService.restoreRule(rule).subscribe((res) => {
      // Set rule to IsActive
      this.rules.find((r) => r === rule).isActive = true;
      this.mdDialog.open(SuccessComponent, {
        data: { message: "Rule has been restored." },
      });
    });
  }

  /**
   * Open the edit rule modal
   * @param {Rule} rule
   */
  editRule(rule: Rule) {
    this.router.navigate([
      { outlets: { modal: ["edit", "rule", rule.id, "edit", rule.for] } },
    ]);
  }

  /**
   * Open the duplicate rule modal
   * @param {Rule} rule
   */
  duplicateRule(rule: Rule) {
    this.router.navigate([
      { outlets: { modal: ["edit", "rule", rule.id, "duplicate", rule.for] } },
    ]);
  }

  /**
   * Sort the rules
   * @param event
   */
  onSort(event) {
    if (this.rules && this.rules.length > 0) {
      if (
        this.rules[0][event.column.prop] &&
        this.rules[0][event.column.prop].orderId
      ) {
        this.loading = true;
        const orderOn = this.rules[0][event.column.prop].orderId;
        this.init();
        this.orderByDescending = event.newValue === "desc";
        this.orderOn = orderOn;
        this.loadRules();
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/magic-box/magic-box-search/magic-box-search.component.ts